import type { MedalScoreThresholds } from "@dailywars/core/db";
import { mapIdToCalendarDate, type Coord, type GameState } from "@dailywars/core/engine";
import { toUtcDateString } from "@dailywars/core/entitlements/mapAccess";
import { projectedMedalTierForScore } from "@dailywars/core/medals";
import { getRouteApi } from "@tanstack/react-router";
import type { RefObject } from "react";

import type { ScrollChromeRefs } from "#/components/game/gameCanvas/boardViewport";
import { GameOverDialog } from "#/components/game/GameOverBanner";
import { MapRating } from "#/components/game/MapRating";
import { ObjectiveDialog } from "#/components/game/ObjectiveDialog";
import { useGameSelector } from "#/game/GameStoreProvider";
import { useRequestGameContext } from "#/lib/useRequestGameContext";

import { PlayMapDockBars } from "./docks";
import { PlayMapHudAndCanvas, PlayMapHudBar } from "./hudCanvas";

const playMapRoute = getRouteApi("/play_/$mapId");

type PlayMapGameCardProps = {
  boardScrollFocus: Coord | null;
  topDockRef: RefObject<HTMLDivElement | null>;
  bottomDockRef: RefObject<HTMLDivElement | null>;
  scrollChrome: ScrollChromeRefs;
  handleUndo: () => void;
  handleEndTurn: () => void | Promise<void>;
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
  boardDialogOpen?: boolean;
  medalThresholds: MedalScoreThresholds | null;
};

function PlayMapStickyTopDock({ topDockRef }: { topDockRef: RefObject<HTMLDivElement | null> }) {
  return (
    <div ref={topDockRef} className="sticky top-[max(0px,env(safe-area-inset-top))] z-30 shrink-0">
      <div className="border-b border-border bg-card shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
        <PlayMapHudBar />
      </div>
    </div>
  );
}

function PlayMapStickyBottomDock({
  bottomDockRef,
  handleUndo,
  handleEndTurn,
  handleRestart,
  isRestarting,
  boardDialogOpen,
}: {
  bottomDockRef: RefObject<HTMLDivElement | null>;
  handleUndo: () => void;
  handleEndTurn: () => void | Promise<void>;
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
  boardDialogOpen?: boolean;
}) {
  return (
    <div
      ref={bottomDockRef}
      className="sticky bottom-[max(0px,env(safe-area-inset-bottom))] z-30 shrink-0"
    >
      <div className="bg-card shadow-[0_-4px_24px_rgba(0,0,0,0.08)]">
        <PlayMapDockBars
          handleUndo={handleUndo}
          handleEndTurn={handleEndTurn}
          handleRestart={handleRestart}
          isRestarting={isRestarting}
          boardDialogOpen={boardDialogOpen}
        />
      </div>
    </div>
  );
}

function PlayMapGameOverIfNeeded({
  handleRestart,
  isRestarting,
  medalThresholds,
}: {
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
  medalThresholds: MedalScoreThresholds | null;
}) {
  const localState = useGameSelector((s) => s.localState);
  const playbackActive = useGameSelector((s) => Boolean(s.aiPlayback));
  const dayNumber = useGameSelector((s) => s.serverDayNumber);
  const { mapId } = playMapRoute.useParams();
  const { entitlements } = useRequestGameContext();

  if (localState.status === "in_progress" || playbackActive) return null;

  const restartLocked = entitlements.tier === "free" && localState.status === "victory";
  const medalsAreLive = mapIdToCalendarDate(mapId) === toUtcDateString(new Date());
  const medalTier =
    localState.status === "victory" && medalThresholds && medalsAreLive
      ? projectedMedalTierForScore(dayNumber, medalThresholds)
      : null;

  return (
    <GameOverDialog
      status={localState.status as Exclude<GameState["status"], "in_progress">}
      dayNumber={dayNumber}
      mapId={mapId}
      state={localState}
      onRestart={handleRestart}
      pending={isRestarting}
      restartLocked={restartLocked}
      medalTier={medalTier}
      extraContent={localState.status === "victory" ? <MapRating mapId={mapId} /> : undefined}
    />
  );
}

export function PlayMapGameCard({
  boardScrollFocus,
  topDockRef,
  bottomDockRef,
  scrollChrome,
  handleUndo,
  handleEndTurn,
  handleRestart,
  isRestarting,
  boardDialogOpen,
  medalThresholds,
}: PlayMapGameCardProps) {
  return (
    <div className="relative flex w-max max-w-full flex-col sm:min-w-[576px]">
      <PlayMapStickyTopDock topDockRef={topDockRef} />
      <PlayMapHudAndCanvas boardScrollFocus={boardScrollFocus} scrollChrome={scrollChrome} />
      <PlayMapStickyBottomDock
        bottomDockRef={bottomDockRef}
        handleUndo={handleUndo}
        handleEndTurn={handleEndTurn}
        handleRestart={handleRestart}
        isRestarting={isRestarting}
        boardDialogOpen={boardDialogOpen}
      />
      <ObjectiveDialog medalThresholds={medalThresholds} />
      <PlayMapGameOverIfNeeded
        handleRestart={handleRestart}
        isRestarting={isRestarting}
        medalThresholds={medalThresholds}
      />
    </div>
  );
}
